import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getReplay } from '../GameLog/actions';
import { CATCH_UP } from './replayModes';
import { selectReplayEntries } from '../GameLog/selectors';
import { catchUpStart, replayStart } from './replayStart';

/**
 * Where a replay of the game is: its moves (each with its board) and the one shown, starting at the first move you
 * haven't seen (`seenVersion`), else the latest move; or, catching up, at the board as you last saw it
 * (catchUpStart). `step(-1)` and `step(1)` go back and forward; `forward` is whether the last step went forward
 * (cards fly then).
 *
 * @param {?string} on null when not replaying; CATCH_UP or REPLAY
 * @returns {{ entries: ?object[], index: ?number, entry: ?object, step: function, forward: boolean }}
 */
export default function useReplay(gameId, seenVersion, on) {
  const dispatch = useDispatch();
  const entries = useSelector((state) => selectReplayEntries(state, { gameId }));
  const [index, setIndex] = useState(null);
  const [forward, setForward] = useState(false);

  // Turning it on loads the moves; going from catching up to stepping by hand keeps the place.
  const started = !!on;
  useEffect(() => {
    if (started) {
      setIndex(null);
      dispatch(getReplay(gameId));
    }
  }, [started, gameId, dispatch]);

  useEffect(() => {
    if (on && entries && index === null) {
      setIndex((on === CATCH_UP ? catchUpStart : replayStart)(entries, seenVersion));
      setForward(false);
    }
  }, [on, entries, index, seenVersion]);

  const last = entries ? entries.length - 1 : 0;
  const step = (by) => {
    setForward(by > 0);
    setIndex(i => Math.max(0, Math.min(last, i + by)));
  };

  const ready = on && entries && index !== null;
  return {
    entries: ready ? entries : null,
    index: ready ? index : null,
    entry: ready && entries.length ? entries[index] : null,
    step,
    forward,
  };
}
